/**
 * HeatMap — 5×5 likelihood × impact risk grid (SSP parity).
 *
 * X-axis: likelihood (1=Rare → 5=Almost certain), left-to-right.
 * Y-axis: impact     (1=Negligible → 5=Catastrophic), bottom-to-top — high
 *         impact appears at the top of the chart, the conventional risk-
 *         matrix orientation.
 *
 * Each cell shows the count of risks at that (likelihood, impact) pair.
 * Cell colour is bucketed by SSP's three-tier scoring (likelihood × impact):
 *   ≤  4  → low      (emerald)
 *   5–15  → medium   (amber)
 *   ≥ 16  → high     (red)
 *
 * Clicking a cell selects it and renders a side panel listing the matching
 * risks. Each row in the panel exposes an edit affordance via the optional
 * `onRiskEdit` prop — G3's RiskAssessmentWorkspace passes a handler that
 * opens RiskFormSlideOver; standalone usage falls back to a no-op.
 *
 * Optional site filter dropdown narrows the heatmap to one site. Pulled
 * from the comparison endpoint's per-site grouping.
 *
 * Linear: MYD-212 (G1). Standalone — embedded by G3 (RiskAssessmentWorkspace)
 * and reusable by C6 (Full Analysis report template).
 *
 * Sidebar IA Lock: this PR does NOT touch AdminPanelCN.tsx.
 */

import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	ShieldAlert,
	X as CloseIcon,
	Pencil,
	Filter,
} from 'lucide-react'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Select } from 'components/ui/select'
import { cn } from 'lib/utils'

import { fetchRisksComparison } from 'slices/riskSlice'

import RiskHeatGrid from './RiskHeatGrid'
import { IMPACT_LABELS, LIKELIHOOD_LABELS, SEVERITY_CLASSES, severityFromScore } from './riskScale'

interface RiskRow {
	_id: string
	name?: string
	description?: string
	category?: string
	likelihood: number
	impact: number
	status?: string
	site: string
	siteName?: string
}

interface HeatMapProps {
	evalId: string
	/** Called when the user taps the edit pencil on a risk row in the side panel. */
	onRiskEdit?: (risk: RiskRow) => void
}

const HeatMap = ({ evalId, onRiskEdit }: HeatMapProps) => {
	const dispatch = useDispatch()

	const comparison: any[] = useSelector(
		(state: any) => state.risks?.comparisonByEvaluation?.[evalId] || [],
	)

	const [selectedSiteId, setSelectedSiteId] = useState<string>('')
	const [selectedCell, setSelectedCell] = useState<{ likelihood: number; impact: number } | null>(
		null,
	)

	useEffect(() => {
		// @ts-ignore — thunk
		dispatch(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	// Flatten the per-site comparison shape into a single risk array, decorated
	// with siteName so the side panel can show "Risk X (Site Y)".
	const allRisks: RiskRow[] = useMemo(() => {
		const flat: RiskRow[] = []
		for (const site of comparison) {
			const sName = site?.siteName ?? '—'
			for (const r of site?.risks ?? []) {
				flat.push({ ...r, site: String(site.siteId), siteName: sName })
			}
		}
		return flat
	}, [comparison])

	const sites = useMemo(
		() =>
			comparison.map((s: any) => ({
				_id: String(s.siteId),
				name: s.siteName,
				riskCount: s.riskCount,
			})),
		[comparison],
	)

	const filteredRisks = useMemo(
		() =>
			selectedSiteId
				? allRisks.filter((r) => r.site === selectedSiteId)
				: allRisks,
		[allRisks, selectedSiteId],
	)

	// Build a map keyed by `${likelihood}-${impact}` → array of risks.
	// Counts come from .length so we don't double-count.
	const cellMap = useMemo(() => {
		const map = new Map<string, RiskRow[]>()
		for (const r of filteredRisks) {
			const L = Number(r.likelihood)
			const I = Number(r.impact)
			if (L < 1 || L > 5 || I < 1 || I > 5) continue
			const key = `${L}-${I}`
			const list = map.get(key) ?? []
			list.push(r)
			map.set(key, list)
		}
		return map
	}, [filteredRisks])

	const cellCounts = useMemo(() => new Map([...cellMap].map(([key, list]) => [key, list.length])), [cellMap])

	const totals = useMemo(() => {
		let low = 0
		let medium = 0
		let high = 0
		for (const r of filteredRisks) {
			const sev = severityFromScore(Number(r.likelihood) * Number(r.impact))
			if (sev === 'low') low++
			else if (sev === 'medium') medium++
			else high++
		}
		return { low, medium, high, total: filteredRisks.length }
	}, [filteredRisks])

	const selectedKey = selectedCell
		? `${selectedCell.likelihood}-${selectedCell.impact}`
		: null
	const selectedRisks = selectedKey ? cellMap.get(selectedKey) ?? [] : []

	const isEmpty = filteredRisks.length === 0

	return (
		<Card>
			<CardHeader className="pb-3 flex-row items-start justify-between gap-3 flex-wrap">
				<div>
					<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
						<ShieldAlert className="w-4 h-4" />
						Risk heat map ({totals.total})
					</h4>
					<p className="text-xs text-gray-500 mt-0.5">
						Likelihood × impact. Click any cell to see the risks in that bucket.
					</p>
				</div>
				<div className="flex items-center gap-2">
					{sites.length > 1 && (
						<div className="flex items-center gap-1.5">
							<Filter className="w-3.5 h-3.5 text-gray-400" aria-hidden />
							<Select
								value={selectedSiteId}
								onChange={(e) => {
									setSelectedSiteId(e.target.value)
									setSelectedCell(null)
								}}
								aria-label="Filter by site"
								className="text-xs"
							>
								<option value="">All sites</option>
								{sites.map((s: any) => (
									<option key={s._id} value={s._id}>
										{s.name} ({s.riskCount})
									</option>
								))}
							</Select>
						</div>
					)}
					<div className="flex items-center gap-1">
						<Badge className={cn('text-[10px] gap-0.5', SEVERITY_CLASSES.low.chip)}>
							Low {totals.low}
						</Badge>
						<Badge className={cn('text-[10px] gap-0.5', SEVERITY_CLASSES.medium.chip)}>
							Med {totals.medium}
						</Badge>
						<Badge className={cn('text-[10px] gap-0.5', SEVERITY_CLASSES.high.chip)}>
							High {totals.high}
						</Badge>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				{/* Heatmap + side panel layout. On mobile the panel stacks below.  */}
				<div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
					{/* Heatmap grid */}
					<div>
						<RiskHeatGrid
							counts={cellCounts}
							selectedCell={selectedCell}
							onSelectCell={cell => setSelectedCell(cell)}
						/>
					</div>

					{/* Side panel — selected cell or empty hint */}
					<div className="lg:border-l lg:border-gray-200 lg:pl-4">
						{selectedCell ? (
							<>
								<div className="flex items-center justify-between mb-2">
									<div>
										<div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
											{LIKELIHOOD_LABELS[selectedCell.likelihood]} × {IMPACT_LABELS[selectedCell.impact]}
										</div>
										<div className="text-xs text-gray-500">
											Score {selectedCell.likelihood * selectedCell.impact} ·{' '}
											{selectedRisks.length} risk{selectedRisks.length === 1 ? '' : 's'}
										</div>
									</div>
									<button
										type="button"
										onClick={() => setSelectedCell(null)}
										aria-label="Close panel"
										className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-50"
									>
										<CloseIcon className="w-3.5 h-3.5" />
									</button>
								</div>
								{selectedRisks.length === 0 ? (
									<p className="text-xs text-gray-400 italic py-3">
										No risks at this likelihood × impact yet.
									</p>
								) : (
									<div className="space-y-2 max-h-[420px] overflow-y-auto">
										{selectedRisks.map((r) => (
											<div
												key={r._id}
												className="rounded-md border border-gray-200 px-2.5 py-2 text-xs space-y-0.5"
											>
												<div className="flex items-start justify-between gap-2">
													<div className="font-medium text-gray-900 truncate flex-1">
														{r.name ?? r.description ?? 'Untitled risk'}
													</div>
													{onRiskEdit && (
														<button
															type="button"
															onClick={() => onRiskEdit(r)}
															aria-label="Edit risk"
															title="Edit risk"
															className="p-0.5 rounded text-gray-400 hover:text-[#0080fe] hover:bg-gray-50 shrink-0"
														>
															<Pencil className="w-3 h-3" />
														</button>
													)}
												</div>
												<div className="text-[10px] text-gray-500 truncate">
													{r.siteName}
													{r.category ? ` · ${r.category}` : ''}
												</div>
												{r.status && (
													<Badge className="text-[9px] bg-gray-100 text-gray-600">
														{r.status}
													</Badge>
												)}
											</div>
										))}
									</div>
								)}
							</>
						) : (
							<div className="text-xs text-gray-400 italic py-8 text-center">
								{isEmpty
									? 'No risks added yet. Risks appear on the heat map as you log them per site.'
									: 'Click any cell to see its risks here.'}
							</div>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	)
}

export default HeatMap
